import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useContext } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import Header from './Header';
import MiddleSection from './MiddleSection';
import CodingSection from './CodingSection';
import InterviewerSection from './InterviewerSection';
import SectionSlider from 'components/CodingAssessment/SectionSlider';
import useCodingStore from './CodingStore';
import TourStartDialog from 'dialogs/TourStartDialog';
import { AssessmentStates, QuestionStates } from './constant';
import { ProctoringContext, TourProviderContext, useLoading, useServices, useSnackbar } from 'contexts';
import { finishChallenge, getCodeChallengeById } from 'services';
import { CodingTourSteps } from './constant';
import { createCodeChallengeAttempt } from 'services/codeChallengeAttemptService';
import {
  getClientPrefix, Session, getCompletedCodingAttempt, markCodingAttemptDone
} from 'utils';
import ActionTimerDialog from 'dialogs/ActionTimerDialog';
import FullscreenPermissionDialog from 'dialogs/FullscreenPermissionDialog';
import AnalysisScreen from './AnalysisScreen';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100vw',
    height: '100vh',
    backgroundColor: '#fff',
    overflow: 'hidden'
  },
  header: {
    height: '76px',
  },
  content: {
    display: 'flex',
    gap: theme.spacing(5),
    height: 'calc(100vh - 76px)',
    justifyContent: 'space-between',
    padding: theme.spacing(5),
    overflow: 'auto',
  },
  quesSection: {
    width: 'calc(100vw - 250px)',
    display: 'flex',
    border: '1px solid',
    borderColor: theme.palette.neutral['clr-200'],
    borderRadius: '4px',
    overflow: 'hidden',
  },
  sectionsWidth: {
    transition: 'width 0.3s ease-in-out',
  }
}));

const CodingAssessment = () => {
  const classes = useStyles();
  const service = useServices();
  const snackbar = useSnackbar();
  const loading = useLoading();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, tagId } = location.state || {};
  const user = Session.userId;
  const cid = searchParams.get("cid");

  // This route creates a brand new attempt on every mount, so landing back on
  // it - browser back from the report, forward, a pasted URL - would spend
  // another attempt, the only one on a contest. A finished attempt leaves a
  // marker; reading it once at mount (not on every render) keeps finishing the
  // current attempt from bouncing this component mid-teardown.
  const completedAttemptId = React.useRef(getCompletedCodingAttempt(cid)).current;

  const [middleContainerWidth, setMiddleContainerWidth] = React.useState(60);
  const [openTourDialog , setOpenTourDialog] = React.useState(false);
  const [openFinalSubmittionDialog , setOpenFinalSubmittionDialog] = React.useState(false);
  const [openFullscreenDialog, setOpenFullscreenDialog] = React.useState(false);
  const [finishing, setFinishing] = React.useState(false);
  const [finished, setFinished] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const [creatingAttempt, setCreatingAttempt] = React.useState(false);

  const { setSteps, play } = React.useContext(TourProviderContext);
  const assessmentState = useCodingStore(state => state.assessmentState);
  const setAssessmentState = useCodingStore(state => state.setAssessmentState);

  const [
    startAssessment, endTutorial, setChallenge, challenge,
    setAttempt, attempt, timeOut, questionState, reset,
  ] = useCodingStore(state => [
    state.startAssessment, state.endTutorial, state.setChallenge, state.challenge,
    state.setAttempt, state.attempt, state.timeOut, state.questionState, state.reset,
  ]);

  React.useEffect(() => {
    reset();
  }, []);

  const {
      endProctoring,
      getProctoringData,
      activateFullScreenProctoring,
      activateGeneralProctoring,
      activateImageProctoring,
      enableFullscreenMode,
      fullScreenActive
  } = useContext(ProctoringContext);

  const fetchChallenge = async()=>{
    try {
      loading.show();

      const result = await service.callService(
          getCodeChallengeById, cid
      );
  
      setChallenge(result);
      setAssessmentState(AssessmentStates.SHOWING_TUTORIAL);
    } catch (error) {
      console.log(error);
      snackbar.error("Uh Oh! Unable to get challenge");
    } finally {
      loading.hide();
    }
  }

  const startAttempt = async()=>{
    if((attempt && Object.keys(attempt).length) || creatingAttempt) return;
    try {
      loading.show();
      setCreatingAttempt(true);

      const newAttempt = await service.callService(
        createCodeChallengeAttempt, {
          user: Session.userId,
          codeChallenges: [challenge._id],
          startedAt: new Date(),
          assessmentEntryPoint: { data, tagId }
        }
      );

      setAttempt(newAttempt);
      setTimeout(() => startAssessment(), 2000);
    } catch (error) {
      console.log(error);
      snackbar.error("Uh Oh! Unable to start the attempt");
    } finally {
      setCreatingAttempt(true);
      loading.hide();
    }
  }

  const handleFinishAssessment = async()=> {
    if(finishing || assessmentState !== AssessmentStates.FINISHING) return;
    try{
      setOpenFinalSubmittionDialog(false);
      setFinishing(true);
      endProctoring();
      const proctorRecords = getProctoringData(true);
      
      const { isActive } = await finishChallenge(attempt._id, {
        finishedAt: new Date(), proctorRecords
      });

      if (isActive) {
        // Only this path produces a report, so only this path may redirect a
        // re-entry to it instead of starting the challenge again.
        markCodingAttemptDone(cid, attempt._id);
        setFinished(true);
      } else {
        reset();
        snackbar.info("Attempt not considered, none of the answers recorded!");
        navigate(
          `/${getClientPrefix(user)}/personalized-interview?type=coding`,
          { replace: true }
        );
      }
    }catch(error){
      console.log(error);
      snackbar.error("Uh Oh! Unable to finish the attempt");
      navigate(
        `/${getClientPrefix(user)}/personalized-interview?type=coding`,
        { replace: true }
      );
    }
  };

  const handleSuccessFullscreenMode = () => {
      enableFullscreenMode();
      setOpenFullscreenDialog(false);
      if(assessmentState === AssessmentStates.FULL_SCREEN_PERMISSION){
        setAssessmentState(AssessmentStates.FETCHING_CHALLENGE);
      }
  };

  React.useEffect(() => {
    if (completedAttemptId) return;

    switch(assessmentState){
      case AssessmentStates.SHOWING_TUTORIAL: {
        const shown = JSON.parse(localStorage.getItem("coding-interview-tour"));
        if(play || openTourDialog) break;
        if(shown && !openTourDialog) endTutorial();
        else{
          setSteps(CodingTourSteps);
          setOpenTourDialog(true);
          localStorage.setItem("coding-interview-tour", "true");
        }
        break;
      }
      case AssessmentStates.FETCHING_CHALLENGE: {
        fetchChallenge();
        break;
      }
      case AssessmentStates.STARTING: {
        startAttempt();
        break;
      }
      case AssessmentStates.RUNNING: {
        activateFullScreenProctoring();
        activateGeneralProctoring();
        activateImageProctoring(20000).catch(console.error);
        break;
      }
      case AssessmentStates.FULL_SCREEN_PERMISSION: {
        setOpenFullscreenDialog(true);
        break;
      }
      case AssessmentStates.FINISHING: {
        handleFinishAssessment();
        break;
      }
      default:
    }
  }, [assessmentState, play, challenge, completedAttemptId]);

  React.useEffect(()=>{
      setOpenFinalSubmittionDialog(timeOut);
  }, [timeOut]);

  React.useEffect(()=>{
    if(questionState === QuestionStates.CODING){
      setMiddleContainerWidth(35);
    }else{
      setMiddleContainerWidth(65);
    }
  }, [questionState]);

  React.useEffect(() => {
    const listener = () => window.history.go(1);

    window.addEventListener('popstate', listener);
    console.log("Added popstate event");

    return () => {
        console.log("Removed popstate event");
        window.removeEventListener('popstate', listener);
        endProctoring();
        reset();
    };
  }, []);

  React.useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    
    const handleUnload = (event) => {
      if(window.location.pathname.includes("/coding-assessment")){
        localStorage.setItem('codingAssessmentLeft', 'true');
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("unload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.addEventListener("unload", handleUnload);
    };
  }, []);

  React.useEffect(() => {
    if (localStorage.getItem('codingAssessmentLeft') === 'true') {
      localStorage.removeItem('codingAssessmentLeft');
      navigate(-1, { replace: true });
    }
  }, [navigate, location.pathname]);

  React.useEffect(() => {
    if (!completedAttemptId) return;

    navigate(
      `/${getClientPrefix(user)}/performance/4c/${completedAttemptId}`,
      { replace: true }
    );
  }, [completedAttemptId, navigate, user]);

  // Re-entered an attempt that is already done: render nothing while the
  // redirect above takes over.
  if (completedAttemptId) return null;

  return finishing
    ? <AnalysisScreen isFeedbackGenerated={finished} entryPoint={data} />
    : (
      <Box className={classes.root}>
        <Box className={classes.header}>
          <Header/>
        </Box>
        <Box className={classes.content}>
          <InterviewerSection/>

          <Box className={classes.quesSection}>
            <Box 
              width={`${middleContainerWidth}%`}
              className={isDragging ? '' : classes.sectionsWidth}
            >
              <MiddleSection/>
            </Box>

            <SectionSlider
              setLeftWidth={setMiddleContainerWidth}
              setDraggingStart={setIsDragging}
            />

            <Box
              width={`${100 - middleContainerWidth}%`} 
              className={isDragging ? '' : classes.sectionsWidth}
            >
              <CodingSection/>
            </Box>
          </Box>
        </Box>

        <TourStartDialog
          open={openTourDialog}
          onClose={setOpenTourDialog}
          showCloseButton={false}
          title="Interview Walkthrough"
          description={
            <>Before proceeding to the mock interview, it's necessary to understand how it works. This walkthrough 
              <strong> will not be repeated</strong>. 
              Please read instructions carefully to avoid any confusion. Good luck!
            </>
          }
          image='https://assets.languify.in/gifs/interviewScreenGif.gif'
          buttonLabel='Let’s get started'
        />
        {
          openFinalSubmittionDialog &&
            <ActionTimerDialog
              open={openFinalSubmittionDialog}
              onClose={()=>setAssessmentState(AssessmentStates.FINISHING)}
              title={`It was great talking to you, ${Session.user.name}`}
              timerMessage = 'Auto-submitting {timeLeft} seconds'
            />
        }

        <FullscreenPermissionDialog
          open={
            openFullscreenDialog || 
            (!fullScreenActive && assessmentState !== AssessmentStates.SHOWING_TUTORIAL)
          } 
          onSuccess={handleSuccessFullscreenMode}
        />
      </Box>
  );
};

export default CodingAssessment;